import Box from '@material-ui/core/Box';
import { ButtonProps } from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useCallback, useMemo, useState } from 'react';
import { NumericField } from '../fields/NumericField';
import { AddToCartButton } from './AddToCartButton';
import { CartItem } from './CartItem';
import { CartItemPackage } from './CartResult';

export interface AddToCartFieldProps extends Omit<ButtonProps, 'size'> {
    loading?: boolean;
    size?: number | string;
    itemsCount?: number;
    defaultQuantity?: number;
    minimumOrder?: number;
    buttonColor?: string;
    cartItem: Omit<CartItem, 'quantity'> & { package?: Partial<CartItemPackage> };
}

const useStyles = makeStyles(
    () => ({
        root: {
            display: 'flex',
        },

        inputHolder: {
            display: 'inline-flex',
            flex: '1 1 auto',
            width: '100%',
        },

        buttonHolder: {
            display: 'inline-flex',
            flex: '1 1 auto',
            width: '100%',
            maxWidth: 380,
            marginLeft: 16,
        },
    }),
    {
        name: 'AddToCartField',
    },
);

export const AddToCartField: React.FC<AddToCartFieldProps> = React.memo<AddToCartFieldProps>(
    ({ cartItem, defaultQuantity = 1, minimumOrder = 0, buttonColor = 'primary' }: AddToCartFieldProps) => {
        const classes = useStyles();
        const [quantity, setQuantity] = useState(defaultQuantity || 1);

        const cartItem$ = useMemo<CartItem>(
            () => ({
                ...cartItem,
                quantity,
            }),
            [cartItem, quantity],
        );

        const handleQuantityChange = useCallback(
            (quantity: number | string) => {
                setQuantity(parseInt(`${quantity || 1}`));
            },
            [setQuantity],
        );

        const handleReset = useCallback(() => {
            setQuantity(defaultQuantity);
        }, [setQuantity, defaultQuantity]);

        return (
            <>
                {minimumOrder > 0 && (
                    <span style={{ display: 'block', marginBottom: 10 }}>
                        Quantity must be greater than or equal to {minimumOrder}
                    </span>
                )}
                <Box className={classes.root}>
                    <Box
                        style={{ border: quantity < minimumOrder ? '1px solid red' : '' }}
                        className={classes.inputHolder}
                    >
                        <NumericField min={1} value={quantity} onChange={handleQuantityChange} width={'100%'} />
                    </Box>
                    <Box className={classes.buttonHolder}>
                        <AddToCartButton
                            buttonColor={buttonColor}
                            disabled={quantity < minimumOrder}
                            cartItem={cartItem$}
                            onAdd={handleReset}
                            fullWidth
                        />
                    </Box>
                </Box>
            </>
        );
    },
);

AddToCartField.displayName = 'AddToCartField';

export default AddToCartField;
